<template>
  <div class="compare-scroll" role="region" aria-label="Comparación de planes" tabindex="0">
    <table class="compare-table">
      <thead>
        <tr>
          <th scope="col" class="compare-table__corner">Qué incluye</th>
          <th
            v-for="column in columns"
            :key="column.id"
            scope="col"
            :class="{ 'is-featured': column.badge }"
          >
            <span v-if="column.badge" class="compare-table__badge">{{ column.badge }}</span>
            <a :href="`#plan-${column.id}`" @click.prevent="$emit('see-plan', column.id)">{{ column.name }}</a>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.label">
          <th scope="row">{{ row.label }}</th>
          <td
            v-for="(value, index) in row.values"
            :key="columns[index].id"
            :class="{ 'is-featured': columns[index].badge, 'is-empty': value === '—' }"
          >
            <span v-if="value === '—'" class="sr-only">No incluido</span>
            <span :aria-hidden="value === '—' ? 'true' : null">{{ value }}</span>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <th scope="row">Precio mensual</th>
          <td v-for="column in columns" :key="column.id" :class="{ 'is-featured': column.badge }">
            <strong>{{ column.price }}</strong>
            <small>{{ column.priceNote }}</small>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import { formatPrice, planComparisonRows, planOffer } from '../../data/planOffer'

const FAMILY_FROM_PEOPLE = 2

export default {
  name: 'PlanComparisonTable',
  props: {
    plans: { type: Array, required: true }
  },
  computed: {
    rows() {
      return planComparisonRows(this.plans)
    },
    columns() {
      return this.plans.map(({ catalog, api }) => {
        const offer = planOffer(catalog, api, { interval: 'month', people: FAMILY_FROM_PEOPLE })
        return {
          id: catalog.id,
          name: catalog.name,
          badge: catalog.badge || null,
          price: `${catalog.fam ? 'Desde ' : ''}$${formatPrice(offer.amount)}`,
          priceNote: catalog.fam ? `${FAMILY_FROM_PEOPLE} personas` : (catalog.unit ? 'por empleado' : 'al mes')
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../styles/variables';

.compare-scroll {
  position: relative;
  overflow-x: auto;
  border: 1px solid $landing-border-light;
  border-radius: 18px;
  background: white;
  box-shadow: 0 12px 32px rgba(13, 43, 69, 0.08);

  &:focus-visible {
    outline: 3px solid $landing-secondary-color;
    outline-offset: 2px;
  }
}

.compare-table {
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.98rem;

  th,
  td {
    padding: 1rem 1.1rem;
    border-bottom: 1px solid $landing-border-light;
    text-align: center;
    vertical-align: middle;
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: 0;
  }

  thead th {
    background: $landing-primary-color;
    color: white;
    font-size: 1.05rem;
    font-weight: 700;

    a {
      color: inherit;
      text-decoration: none;

      &:hover,
      &:focus-visible {
        text-decoration: underline;
      }
    }
  }

  thead th.is-featured {
    background: $landing-primary-dark;
    box-shadow: inset 0 4px 0 $landing-secondary-color;
  }

  tbody th,
  tfoot th {
    position: sticky;
    left: 0;
    z-index: 1;
    background: white;
    color: $landing-primary-color;
    font-weight: 600;
    text-align: left;
    box-shadow: 1px 0 0 $landing-border-light;
  }

  .compare-table__corner {
    position: sticky;
    left: 0;
    z-index: 2;
    text-align: left;
  }

  td {
    color: $landing-text-dark;
  }

  td.is-featured {
    background: rgba(34, 207, 224, 0.07);
  }

  td.is-empty {
    color: $landing-text-light;
  }

  tfoot th,
  tfoot td {
    border-top: 2px solid $landing-primary-color;
    border-bottom: 0;
    background: $landing-bg-light;
  }

  tfoot td.is-featured {
    background: rgba(34, 207, 224, 0.14);
  }

  tfoot strong {
    display: block;
    color: $landing-primary-color;
    font-size: 1.35rem;
    font-variant-numeric: tabular-nums;
  }

  tfoot small {
    color: $landing-text-medium;
  }
}

.compare-table__badge {
  display: block;
  margin-bottom: 4px;
  color: $landing-secondary-color;
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
</style>
